import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpParameterCodec, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError as observableThrowError, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { IndexedDBService } from 'libs/api/src/lib/services/indexed-db.service';
import { LoadingService } from 'libs/shared/src/lib/service/loading.service';
import { MatDialog } from '@angular/material/dialog';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { StorageService } from '../../../../base/src/lib/services/storage.service';
import { GlobalToastService } from 'libs/global-toast/src/lib/services/global-toast.service';
import { panelConstants } from 'libs/api/src/lib/constants/panel.api.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService implements HttpInterceptor {
  whiteListLogoutUrls = [panelConstants.API_USER_REFERENCE_KEYS];
  languageKey = 'en';
  private get oauthService() {
    return this.injector.get(OAuthService);
  }
  private oathToken: any;  
  constructor(
    public storageService: StorageService,
    private _dialog: MatDialog,
    private router: Router,
    private _loadingService: LoadingService,
    private indexedDBService: IndexedDBService,
    private injector: Injector,
    private _globalToastService: GlobalToastService
  ) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const accessToken = this.storageService.getStorageItem('accessToken');
    this.oathToken = this.storageService.getStorageItem('access_token');
    const currentCustomerId = this.storageService.getStorageItem('customerId');
    this.languageKey = this.storageService.getStorageItem('lang') || 'en';

    const skipHeader = req.headers.get('skip');
    const skipSubpanelHeader = req.headers.get('skip-current-subpanel-ids');

    if (accessToken && !skipHeader) {
      const url = req.url;
      const params = new HttpParams({
        encoder: new CustomEncoder(),
        fromString: req.params.toString(),
      });

      const isStaticAsset =
        url.indexOf('json') > -1 ||
        url.indexOf('svg') > -1 ||
        url.indexOf('openid-configuration') > -1 ||
        url.indexOf('openid-connect') > -1;

      if (skipSubpanelHeader) {
        // Request has 'skip-current-subpanel-ids' header:
        // Attach auth headers WITHOUT subpanel-id and Accept-Language,
        // and remove the 'skip-current-subpanel-ids' header before forwarding.
        const headers = req.headers.delete('skip-current-subpanel-ids');
        if (isStaticAsset) {
          req = req.clone({ params });
        } else {
          req = req.clone({
            headers: headers
              .set('Authorization', `Bearer ${accessToken}`)
              .set('current-customer-id', currentCustomerId ? currentCustomerId : ''),
            params,
          });
        }
      } else {
        // Normal request: attach full headers including subpanel-id and Accept-Language.
        const subpanelId = this.storageService.getStorageItem('subpanelId');
        if (isStaticAsset) {
          req = req.clone({ params });
        } else {
          req = req.clone({
            setHeaders: {
              Authorization: `Bearer ${accessToken}`,
              'current-customer-id': `${currentCustomerId ? currentCustomerId : ''}`,
              'current-subpanel-id': `${subpanelId ? subpanelId : ''}`,
              'Accept-Language': this.languageKey,
            },
            params,
          });
        }
      }
    } else if (skipHeader) {
      // 'skip' is an app-internal flag used to bypass auth headers; remove it
      // before the request goes out so external hosts (e.g. S3 presigned URLs)
      // don't receive an unexpected custom header that fails CORS preflight.
      req = req.clone({ headers: req.headers.delete('skip') });
    }

    return next.handle(req).pipe(
      tap((res) => {
        if (res instanceof HttpResponse) {
        }
      }),
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse && err.error.status) {
          switch (err.status) {
            case 400:
              if (err.message) {
                this._globalToastService.showToast(err?.error?.notifications);	
              }
              break;
            case 401:
              if (!(this.whiteListLogoutUrls.some((v) => err?.url?.includes(v)))) {
                if (err.message) {
                  this._globalToastService.showToast(err?.error?.notifications);
                }
                this.logout();
              }
              break;
            case 403:
              if (err.message) {
                this._globalToastService.showToast(err?.error?.notifications);
              }
              break;
            case 404:
              if (err.message) {
                this._globalToastService.showToast(err?.error?.notifications);	
              }
              break;
            case 409:
              if (err.message) {
                this._globalToastService.showToast(err?.error?.notifications);
              }
              break;
            case 422:
              if (!err?.error?.additional_error_data) {
                // Show toast even when additional_data is set (e.g. button_disable);
                // still rethrow so callers can react (disable save, etc.).
                if (err?.error?.notifications?.length) {
                  this._globalToastService.showToast(err.error.notifications);
                }
                if (err?.error?.additional_data) {
                  return observableThrowError(err);
                }
              }
              break;
            case 500:
              if (err.message) {
                this._globalToastService.showToast(err?.error?.notifications);
              }
              break;
            default:
              return observableThrowError(err);
          }
          this._loadingService.isLoaderVisible.next('hide');
        }
        return observableThrowError(err);
        // return of(err);
      })
    );
  }

  logout() {
    if (this.indexedDBService['db']) {
      this.indexedDBService.closeConnection();
      this.indexedDBService.deleteDB();
      this.indexedDBService.deleteDataBase();
    }
    const oAuthService = this.storageService.getStorageItem('oAuthService');

    if (oAuthService || this.oathToken) {
      this.storageService.store('customerId', null);
      this.storageService.clearAll();
      this.storageService.store('oAuthService', true);
      this._dialog.closeAll();
      this.oauthService.logOut();
      document.location.reload();
    } else {
      this.storageService.clearAll();
      this._dialog.closeAll();
      const url = window.location.href;
      url.includes('installer') ? this.router.navigate(['/login']) : document.location.reload();
    }
  }
}

class CustomEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}
